/**
 * Customer ledger filtering (SCRUM-206), ported from Ryan's `getFilteredCustomers`.
 *
 * SCRUM-977 — the per-column filters now match Ryan's `app-shell 7b.html`
 * `COLUMN_FILTERS` design EXACTLY: single-select predefined BUCKETS per column
 * (replacing the SCRUM-867 free min/max range inputs), plus Name / Match / Last
 * filters Ryan defines. Each column filter holds ONE selected option value (or
 * null). The option catalogues + predicates are transcribed verbatim from
 * 7b.html L10777-10889; the branch/region filters are our SCRUM-956 addition,
 * kept and styled into the same menu (Ryan has Branch sort-only, no Region).
 */
import type { Customer } from '@/features/intelligence-dashboard/customer.schema';
import type { Product } from '@/features/product-scan/product-scan.schema';

import { customerHolderAge } from '../customer-derived';
import { customerEligibility } from '../customer-eligibility';
import type { InsightType } from '@/features/intelligence-dashboard/insight.types';

/** A half-open numeric bucket: matches `value >= min && value < max` (Ryan). */
export interface NumericBucket {
  min: number;
  max: number;
}
/** An inclusive count bucket (holdings): matches `count >= min && count <= max` (Ryan). */
export interface CountBucket {
  min: number;
  max: number;
}
/** A first-letter range bucket for Name: matches `first >= from && first < to` (Ryan). */
export interface LetterBucket {
  from: string;
  to: string;
}
/** A best-eligibility-ratio bucket for Match (Ryan). */
export interface MatchBucket {
  min: number;
  max?: number;
  strict?: boolean;
}
/** A last-contact bucket for Last: `{max}` days, or the 'never' sentinel (Ryan). */
export type LastBucket = { max: number } | 'never';

export interface LedgerFilters {
  /** Free-text query matched against customer name and portfolio id. */
  search: string;
  /** Active insight types — a customer must have ALL of these active. */
  activeInsights: InsightType[];
  /** 'with' = only customers with a last campaign; 'without' = only those without. */
  activity: 'all' | 'with' | 'without';
  /** Products the customer must be eligible for (ALL of them). Drives the Match column. */
  productMatches: Product[];

  // ── SCRUM-977 per-column filters — Ryan's exact single-select buckets ──────
  // null means "no constraint", so EMPTY_FILTERS stays a no-op and pages that
  // never set these (the teller Customer Dashboard) behave as before.

  /** Name first-letter bucket (Ryan: A–E … U–Z). */
  name: LetterBucket | null;
  /** Account type. Single-select (Ryan: Personal / Business). */
  type: Customer['accountType'] | null;
  /** Tenure-in-months bucket (Ryan `age`: <6mo … >60). */
  tenure: NumericBucket | null;
  /** Derived holder-age bucket (Ryan: Under 30 … 65+). Excludes Business (null age). */
  holderAge: NumericBucket | null;
  /** Balance bucket (Ryan: <$10K … >$200K). */
  balance: NumericBucket | null;
  /** Holdings-count bucket (Ryan: 1 … 5+ products). */
  holdings: CountBucket | null;
  /**
   * Signals filter — single-select (Ryan: Has any / None / <each type>).
   * Deliberately a SEPARATE field from `activeInsights`, which the Product
   * Insights panel owns; the two controls must never write each other's state
   * (SCRUM-867 RT1). The effective constraint is AND-composed.
   */
  signals: 'has' | 'none' | InsightType | null;
  /** Best-product-match bucket (Ryan: Perfect / High / Partial / Low). No-op when no product active. */
  match: MatchBucket | null;
  /** Last-contact bucket (Ryan: <14d / <30d / Never). */
  last: LastBucket | null;

  // ── SCRUM-956 geography (kept; Ryan has Branch sort-only, no Region) ───────
  /** Branch — single-select of a tenant branch. */
  branch: string | null;
  /** Region — single-select of a region. */
  region: string | null;
}

export const EMPTY_FILTERS: LedgerFilters = {
  search: '',
  activeInsights: [],
  activity: 'all',
  productMatches: [],
  name: null,
  type: null,
  tenure: null,
  holderAge: null,
  balance: null,
  holdings: null,
  signals: null,
  match: null,
  last: null,
  branch: null,
  region: null,
};

/**
 * The SCRUM-977 column-filter slice of {@link LedgerFilters} — the fields a user
 * sets from the table headers, as opposed to the search box and the Product
 * Insights panel. Kept as its own type so `CampaignsPage` can hold exactly this
 * in state and spread it, without ever overwriting `activeInsights` /
 * `productMatches` (which the panel owns — the RT1 ownership separation).
 */
export type ColumnFilters = Pick<
  LedgerFilters,
  | 'name'
  | 'type'
  | 'tenure'
  | 'holderAge'
  | 'balance'
  | 'holdings'
  | 'signals'
  | 'match'
  | 'last'
  | 'branch'
  | 'region'
>;

/** Column-filter keys in ledger display order — the single source the chip row,
 * the every-key guard, and hasActiveColumnFilters all iterate. */
export const COLUMN_FILTER_KEYS: readonly (keyof ColumnFilters)[] = [
  'name',
  'type',
  'holderAge',
  'tenure',
  'branch',
  'region',
  'balance',
  'holdings',
  'signals',
  'match',
  'last',
];

/** No column filters applied. */
export const EMPTY_COLUMN_FILTERS: ColumnFilters = {
  name: null,
  type: null,
  tenure: null,
  holderAge: null,
  balance: null,
  holdings: null,
  signals: null,
  match: null,
  last: null,
  branch: null,
  region: null,
};

/** True when any column filter is active (drives the active-filters row). */
export function hasActiveColumnFilters(f: ColumnFilters): boolean {
  return COLUMN_FILTER_KEYS.some((k) => f[k] !== null);
}

/** Best eligibility ratio of a customer across the active product filters (Ryan's Match test). */
function bestMatchRatio(c: Customer, products: Product[]): number {
  let best = 0;
  for (const p of products) {
    const e = customerEligibility(c, p);
    if (e.total > 0) {
      const r = e.met / e.total;
      if (r > best) best = r;
    }
  }
  return best;
}

/** True when a customer passes every active filter (AND semantics). */
export function customerMatchesFilters(c: Customer, filters: LedgerFilters): boolean {
  // Active insights — must have ALL selected insights active (panel-owned).
  if (filters.activeInsights.length > 0) {
    const allActive = filters.activeInsights.every((k) => c.insights[k]?.active);
    if (!allActive) return false;
  }

  // Product match — must be eligible for ALL (panel-owned).
  if (filters.productMatches.length > 0) {
    const allEligible = filters.productMatches.every((p) => {
      const e = customerEligibility(c, p);
      return e.forcedAllMet && e.met >= p.requiredMatches;
    });
    if (!allEligible) return false;
  }

  // Activity status.
  if (filters.activity === 'with' && !c.lastCampaign) return false;
  if (filters.activity === 'without' && c.lastCampaign) return false;

  // ── SCRUM-977 column filters — Ryan's exact predicates, each AND-composed ──

  // Name — first-letter bucket, `first >= from && first < to`.
  if (filters.name) {
    const first = (c.name[0] ?? '').toUpperCase();
    if (!(first >= filters.name.from && first < filters.name.to)) return false;
  }

  // Type — single account type.
  if (filters.type && c.accountType !== filters.type) return false;

  // Holder age — DERIVED, null for Business (excluded). Half-open `>= min && < max`.
  if (filters.holderAge) {
    const age = customerHolderAge(c);
    if (age == null) return false;
    if (!(age >= filters.holderAge.min && age < filters.holderAge.max)) return false;
  }

  // Tenure (months) — half-open bucket.
  if (filters.tenure && !(c.ageMo >= filters.tenure.min && c.ageMo < filters.tenure.max)) {
    return false;
  }

  // Balance — half-open bucket.
  if (filters.balance && !(c.balance >= filters.balance.min && c.balance < filters.balance.max)) {
    return false;
  }

  // Holdings — INCLUSIVE count bucket `>= min && <= max`.
  if (filters.holdings) {
    const n = c.holdings.length;
    if (!(n >= filters.holdings.min && n <= filters.holdings.max)) return false;
  }

  // Signals — single-select: 'has' any active, 'none', or a specific active type.
  if (filters.signals) {
    const anyActive = (Object.keys(c.insights) as InsightType[]).some((k) => c.insights[k]?.active);
    if (filters.signals === 'has') {
      if (!anyActive) return false;
    } else if (filters.signals === 'none') {
      if (anyActive) return false;
    } else if (!c.insights[filters.signals]?.active) {
      return false;
    }
  }

  // Match — best eligibility ratio across the ACTIVE product filters. No-op when
  // no product is active (Ryan: `if (!products.length) return true`).
  if (filters.match && filters.productMatches.length > 0) {
    const best = bestMatchRatio(c, filters.productMatches);
    const m = filters.match;
    if (m.strict) {
      if (best < 0.999) return false;
    } else if (m.max !== undefined) {
      if (!(best >= m.min && best < m.max)) return false;
    } else if (best < m.min) {
      return false;
    }
  }

  // Last — 'never' or contacted within `max` days.
  if (filters.last) {
    if (filters.last === 'never') {
      if (c.lastCampaign) return false;
    } else if (!(c.lastCampaign && c.lastCampaign.daysAgo <= filters.last.max)) {
      return false;
    }
  }

  // Geography (SCRUM-956) — single-select branch / region.
  if (filters.branch && c.branch !== filters.branch) return false;
  if (filters.region && c.region !== filters.region) return false;

  // Free-text search over name + portfolio id (case-insensitive).
  const query = filters.search.trim().toLowerCase();
  if (query.length > 0) {
    const inName = c.name.toLowerCase().includes(query);
    const inPortfolio = c.portfolioId.toLowerCase().includes(query);
    if (!inName && !inPortfolio) return false;
  }

  return true;
}

/** Applies all filters to the full customer set. */
export function filterCustomers(customers: Customer[], filters: LedgerFilters): Customer[] {
  return customers.filter((c) => customerMatchesFilters(c, filters));
}
